'use client'

import { LoadingRegion } from '@/components/ui/LoadingRegion'
import { useEffect, useRef, useState } from 'react'
import { fetchAd, trackClick } from '../api'
import type { AdPublic } from '../types'

type Props = {
  /** Pre-resolved ad (resolved server-side). When set, no client fetch happens. */
  ad?: AdPublic | null
  /** Zone to fetch client-side. Used when `ad` is not pre-resolved. */
  zoneId?: number
  height: number
  width?: number
  adsenseSlot?: string
  adsensePublisher?: string
  /** Responsive Google slot with a fixed size per breakpoint. */
  adsenseResponsiveSize?: 'leaderboard'
  /** Bottom hairline separator. On by default; off for the inline block. */
  bordered?: boolean
}

export function AdBanner({
  ad: adProp,
  zoneId,
  height,
  width,
  adsenseSlot,
  adsensePublisher,
  adsenseResponsiveSize,
  bordered = true,
}: Props) {
  const [ad, setAd] = useState<AdPublic | null | 'loading'>(
    adProp !== undefined ? adProp : zoneId !== undefined ? 'loading' : null,
  )
  const [adsenseFilled, setAdsenseFilled] = useState<boolean | null>(null)
  const insRef = useRef<HTMLModElement>(null)

  useEffect(() => {
    // Pre-resolved ad or pure AdSense slot: nothing to fetch.
    if (adProp !== undefined || zoneId === undefined) return
    let cancelled = false
    fetchAd(zoneId).then(value => { if (!cancelled) setAd(value) })
    return () => { cancelled = true }
  }, [zoneId, adProp])

  useEffect(() => {
    if (ad !== null || !adsenseSlot || !insRef.current) return

    const observer = new MutationObserver(() => {
      const status = insRef.current?.getAttribute('data-ad-status')
      if (status === 'filled') setAdsenseFilled(true)
      else if (status === 'unfilled') setAdsenseFilled(false)
    })
    observer.observe(insRef.current, { attributes: true, attributeFilter: ['data-ad-status'] })

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(window as any).adsbygoogle = (window as any).adsbygoogle || []
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(window as any).adsbygoogle.push({})
    } catch {
      // AdSense not loaded
    }

    return () => observer.disconnect()
  }, [ad, adsenseSlot])

  useEffect(() => {
    if (!ad || ad === 'loading') return
    window._paq = window._paq || []
    window._paq.push(['trackContentImpression', ad.name, ad.image_url ?? 'text', ad.click_url ?? ''])
  }, [ad])

  async function handleClick(e: React.MouseEvent) {
    e.preventDefault()
    if (!ad || ad === 'loading') return
    window._paq = window._paq || []
    window._paq.push(['trackContentInteraction', 'click', ad.name, ad.image_url ?? 'text', ad.click_url ?? ''])
    // Tracking failure must never swallow the click: fall back to the ad's
    // click_url with the same target behavior as the tracked redirect.
    let url: string | null = null
    try {
      url = await trackClick(ad.id)
    } catch {
      url = null
    }
    const target = url ?? ad.click_url
    if (target) window.open(target, '_blank', 'noopener,noreferrer')
  }

  const isFixedSize = width !== undefined
  const usesBreakpointSizedSlot = adsenseResponsiveSize !== undefined

  if (ad === 'loading') {
    return (
      <div
        style={isFixedSize ? { width, height } : { height }}
        className={isFixedSize ? 'mx-auto' : 'w-full'}
      >
        <LoadingRegion pending label="Anzeige wird geladen…" />
      </div>
    )
  }

  if (ad === null) {
    if (!adsenseSlot || !adsensePublisher) return null
    // Fill status is only known client-side, after the real AdSense script
    // runs — collapse fully (no label, no box) rather than reserve a slot
    // the ad network never filled.
    if (adsenseFilled === false) return null

    const ins = (
      <ins
        ref={insRef}
        className={`adsbygoogle${usesBreakpointSizedSlot ? ' w-full h-full' : ''}`}
        style={isFixedSize ? { display: 'inline-block', width, height } : { display: 'block' }}
        data-ad-client={adsensePublisher}
        data-ad-slot={adsenseSlot}
        data-ad-format={isFixedSize || usesBreakpointSizedSlot ? undefined : 'auto'}
        data-full-width-responsive={isFixedSize || usesBreakpointSizedSlot ? undefined : 'true'}
      />
    )

    if (isFixedSize) {
      return (
        <div className="mx-auto" style={{ width }}>
          <p className="text-center font-mono text-[10px] uppercase tracking-widest text-ink/50 mb-2">
            Anzeige
          </p>
          {ins}
        </div>
      )
    }

    return (
      <div
        data-testid={usesBreakpointSizedSlot ? 'adsense-leaderboard' : undefined}
        style={usesBreakpointSizedSlot ? undefined : { height: adsenseFilled ? height : undefined }}
        className={usesBreakpointSizedSlot ? 'adsense-leaderboard mx-auto overflow-hidden' : 'w-full overflow-hidden'}
      >
        {ins}
      </div>
    )
  }

  return (
    <div
      style={isFixedSize ? { width, height } : { height }}
      className={`relative overflow-hidden${isFixedSize ? ' mx-auto' : ' w-full'}${bordered ? ' border-b border-ink' : ''}`}
    >
      <span className="absolute top-1 right-2 font-mono text-[8px] uppercase tracking-widest text-ink/40 z-10 bg-paper/80 px-1">
        Anzeige
      </span>
      <a
        href={ad.click_url ?? '#'}
        onClick={handleClick}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full h-full relative"
      >
        {ad.image_url && (
          <img
            src={ad.image_url}
            alt={ad.name}
            width={width ?? 290}
            height={height}
            decoding="async"
            className={`relative z-10 h-full object-contain${isFixedSize ? '' : ' w-full'}`}
          />
        )}
      </a>
    </div>
  )
}
